/**
 * PlayersLivePanel — §2 Admin Live Editing.
 *
 * Admin picks a player from the list and can edit, live:
 *   • core fields (level, xp, gold, hp, class, stats)
 *   • skills (level + xp per skill; SKILL_STAT_BONUS re-applies server-side)
 *   • equipment (equip / unequip / destroy any slot)
 *   • inventory (grant a gm_items entry, remove an inventory item)
 *
 * All mutations write straight to the character doc so combat, hero and
 * inventory pull the new values automatically.
 */

import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Save, RefreshCw, Search, Swords, Shield, Gem,
  Package, Sparkles, Trash2, Plus, ChevronDown,
} from "lucide-react";

const STATS = ["strength", "defense", "dexterity", "intelligence", "charisma", "concentration"];
const SKILLS = ["woodcutting", "mining", "fishing", "gathering", "crafting", "enchanting"];
const SLOT_META = {
  weapon:  { label: "Arma",       Icon: Swords },
  armor:   { label: "Armatura",   Icon: Shield },
  trinket: { label: "Accessorio", Icon: Gem },
};

export default function PlayersLivePanel() {
  const [players, setPlayers] = useState([]);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);        // full character (server truth)
  const [draft, setDraft] = useState(null);          // editable copy for core fields
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("core");            // core | skills | equipment | inventory
  const [gmItems, setGmItems] = useState([]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [r, gi] = await Promise.all([api.gmPlayersList(), api.gmItems()]);
      setPlayers(r.players || []);
      setGmItems(gi.items || []);
    } catch (err) {
      toast.error("Errore nel caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = players.filter(p =>
    !q || (p.name || "").toLowerCase().includes(q.toLowerCase())
       || (p.email || "").toLowerCase().includes(q.toLowerCase())
  );

  const loadDetail = async (user_id) => {
    setSelectedId(user_id);
    setDetail(null);
    setDraft(null);
    setTab("core");
    try {
      const d = await api.gmPlayerDetail(user_id);
      setDetail(d);
      setDraft({
        user_id,
        level: d.level, xp: d.xp || 0, gold: d.gold || 0,
        hp: d.hp || 0, max_hp: d.max_hp || 0,
        chosen_class: d.chosen_class || "",
        stats: { ...(d.stats || {}) },
      });
    } catch (err) {
      toast.error("Impossibile caricare il giocatore");
    }
  };

  const savedRefresh = async (silent = false) => {
    if (!selectedId) return;
    const d = await api.gmPlayerDetail(selectedId);
    setDetail(d);
    if (!silent) {
      setDraft(prev => prev ? { ...prev,
        level: d.level, xp: d.xp || 0, gold: d.gold || 0,
        hp: d.hp || 0, max_hp: d.max_hp || 0,
        chosen_class: d.chosen_class || "",
        stats: { ...(d.stats || {}) },
      } : prev);
    }
    // refresh list summary too
    const list = await api.gmPlayersList();
    setPlayers(list.players || []);
  };

  const saveCore = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await api.gmPatchPlayer({
        user_id: draft.user_id,
        level: draft.level, xp: draft.xp, gold: draft.gold,
        hp: draft.hp, max_hp: draft.max_hp,
        chosen_class: draft.chosen_class || null,
        stats: draft.stats,
      });
      toast.success("Salvato");
      await savedRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aggiornamento fallito");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="gm-players-live">
      <div className="flex items-center gap-2">
        <div>
          <div className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A] flex items-center gap-2">
            <Users className="w-4 h-4" /> Giocatori (live)
          </div>
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">
            {players.length} giocatori · modifiche istantanee su combat/hero/inventory
          </div>
        </div>
        <button data-testid="players-refresh" onClick={refresh} disabled={loading}
          className="ml-auto px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase flex items-center gap-1.5 disabled:opacity-50">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Ricarica
        </button>
      </div>

      <div className="flex items-center gap-2 panel p-2">
        <Search className="w-3 h-3 text-[#666]" />
        <input data-testid="players-search" value={q} onChange={e => setQ(e.target.value)}
          placeholder="Cerca per nome/email..."
          className="flex-1 bg-transparent border-none text-xs text-[#E2DFD8] font-mono outline-none" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Left: list */}
        <div className="space-y-1 max-h-[75vh] overflow-y-auto pr-1">
          {filtered.length === 0 ? (
            <div className="panel p-4 text-center text-xs italic text-[#666]">Nessun giocatore.</div>
          ) : filtered.map(p => (
            <button key={p.user_id} type="button"
              data-testid={`player-row-${p.user_id}`}
              onClick={() => loadDetail(p.user_id)}
              className={`w-full text-left panel-inner p-2 border transition-colors ${
                selectedId === p.user_id
                  ? "border-[#E5C34A] bg-[#B89C3A]/10"
                  : "border-[#262626] hover:border-[#B89C3A]"
              }`}>
              <div className="flex items-center gap-2">
                <span className="font-heading text-[12px] text-[#E5C34A] truncate">{p.name || "?"}</span>
                <span className="font-mono text-[9px] text-[#8c8c8c] ml-auto">Lv {p.level ?? "?"}</span>
              </div>
              <div className="font-mono text-[9px] text-[#666] truncate">{p.email || p.user_id}</div>
              <div className="font-mono text-[9px] text-[#B0ADA5] mt-1">
                {p.chosen_class || "—"} · {p.gold || 0}g · HP {p.hp || 0}/{p.max_hp || 0}
                {p.titles?.length > 0 && <span className="text-[#E5C34A]"> · {p.titles.join(", ")}</span>}
              </div>
            </button>
          ))}
        </div>

        {/* Right: editor */}
        <div className="panel p-3 sticky top-2 max-h-[75vh] overflow-y-auto" data-testid="player-editor">
          {!draft || !detail ? (
            <div className="text-center text-xs italic text-[#666] py-12">
              {selectedId ? "Caricamento…" : "Seleziona un giocatore per modificarlo."}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="font-heading text-[12px] tracking-[0.3em] uppercase text-[#E5C34A]">
                {detail.name || draft.user_id}
                <span className="ml-2 font-mono text-[9px] text-[#8c8c8c] normal-case tracking-normal">
                  · {detail.chosen_class || "senza classe"}
                </span>
              </div>

              {/* Tabs */}
              <div className="flex flex-wrap gap-1 border-b border-[#262626] pb-1">
                {[
                  ["core", "Base"],
                  ["skills", "Skill"],
                  ["equipment", "Equipaggiamento"],
                  ["inventory", "Inventario"],
                ].map(([key, label]) => (
                  <button key={key} onClick={() => setTab(key)}
                    data-testid={`tab-${key}`}
                    className={`px-2 py-1 font-heading text-[10px] tracking-widest uppercase border transition-colors ${
                      tab === key
                        ? "border-[#E5C34A] text-[#E5C34A] bg-[#B89C3A]/10"
                        : "border-[#262626] text-[#8c8c8c] hover:border-[#B89C3A]"
                    }`}>{label}</button>
                ))}
              </div>

              {tab === "core" && (
                <CoreEditor draft={draft} setDraft={setDraft} onSave={saveCore} busy={busy} />
              )}
              {tab === "skills" && (
                <SkillsEditor userId={draft.user_id} skills={detail.skills || {}}
                  onChanged={savedRefresh} />
              )}
              {tab === "equipment" && (
                <EquipmentEditor userId={draft.user_id} equipped={detail.equipped || {}}
                  inventory={detail.inventory || []} onChanged={savedRefresh} />
              )}
              {tab === "inventory" && (
                <InventoryEditor userId={draft.user_id} inventory={detail.inventory || []}
                  gmItems={gmItems} onChanged={savedRefresh} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-editors ─────────────────────────────────────────────────

function CoreEditor({ draft, setDraft, onSave, busy }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Livello" value={draft.level} onChange={v => setDraft({ ...draft, level: v })} testid="edit-level" />
        <NumField label="XP" value={draft.xp} onChange={v => setDraft({ ...draft, xp: v })} testid="edit-xp" />
        <NumField label="Oro" value={draft.gold} onChange={v => setDraft({ ...draft, gold: v })} testid="edit-gold" />
        <TextField label="Classe" value={draft.chosen_class} onChange={v => setDraft({ ...draft, chosen_class: v })} testid="edit-class" />
        <NumField label="HP" value={draft.hp} onChange={v => setDraft({ ...draft, hp: v })} testid="edit-hp" />
        <NumField label="HP MAX" value={draft.max_hp} onChange={v => setDraft({ ...draft, max_hp: v })} testid="edit-maxhp" />
      </div>
      <div className="panel-inner p-2 border border-[#333]">
        <div className="font-mono text-[9px] tracking-widest uppercase text-[#E5C34A] mb-2">Statistiche</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {STATS.map(s => (
            <NumField key={s} label={s} value={draft.stats[s] || 0}
              onChange={v => setDraft({ ...draft, stats: { ...draft.stats, [s]: v } })}
              testid={`edit-stat-${s}`} />
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
        <button data-testid="player-save" onClick={onSave} disabled={busy}
          className="px-3 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10 disabled:opacity-40 flex items-center gap-1">
          <Save className="w-3 h-3" /> {busy ? "Salvo…" : "Salva base"}
        </button>
      </div>
    </div>
  );
}

function SkillsEditor({ userId, skills, onChanged }) {
  const [rows, setRows] = useState(() =>
    SKILLS.reduce((acc, k) => { acc[k] = { level: skills?.[k]?.level ?? 1, xp: skills?.[k]?.xp ?? 0 }; return acc; }, {})
  );
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    setRows(SKILLS.reduce((acc, k) => { acc[k] = { level: skills?.[k]?.level ?? 1, xp: skills?.[k]?.xp ?? 0 }; return acc; }, {}));
  }, [skills]);

  const saveOne = async (skill) => {
    setBusy(skill);
    try {
      await api.gmPlayerSetSkill(userId, skill, rows[skill].level, rows[skill].xp);
      toast.success(`${skill}: aggiornato`);
      await onChanged(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore skill");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-2" data-testid="skills-editor">
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c]">
        Enchanting Lv 3+ → +2 Concentrazione ogni 3 livelli (deterministico).
      </div>
      {SKILLS.map(sk => (
        <div key={sk} className="panel-inner p-2 border border-[#333] flex items-end gap-2">
          <div className="font-heading text-[11px] text-[#E5C34A] capitalize w-24 shrink-0">{sk}</div>
          <NumField label="Lv" value={rows[sk].level}
            onChange={v => setRows({ ...rows, [sk]: { ...rows[sk], level: v } })}
            testid={`skill-lv-${sk}`} />
          <NumField label="XP" value={rows[sk].xp}
            onChange={v => setRows({ ...rows, [sk]: { ...rows[sk], xp: v } })}
            testid={`skill-xp-${sk}`} />
          <button onClick={() => saveOne(sk)} disabled={busy === sk}
            data-testid={`skill-save-${sk}`}
            className="px-2 py-1.5 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[9px] tracking-widest uppercase hover:bg-[#4A5D23]/10 disabled:opacity-40 flex items-center gap-1 h-fit">
            <Save className="w-3 h-3" /> {busy === sk ? "…" : "Salva"}
          </button>
        </div>
      ))}
    </div>
  );
}

function EquipmentEditor({ userId, equipped, inventory, onChanged }) {
  const [busy, setBusy] = useState(null);
  const invBySlot = useMemo(() => {
    const out = { weapon: [], armor: [], trinket: [] };
    for (const it of inventory) if (out[it.slot]) out[it.slot].push(it);
    return out;
  }, [inventory]);

  const doEquip = async (item_id) => {
    setBusy(item_id);
    try {
      await api.gmPlayerEquip(userId, item_id);
      toast.success("Equipaggiato");
      await onChanged(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(null); }
  };
  const doUnequip = async (slot) => {
    setBusy(`u-${slot}`);
    try {
      await api.gmPlayerUnequip(userId, slot);
      toast.success("Rimosso");
      await onChanged(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(null); }
  };
  const doDestroy = async (item_id) => {
    if (!window.confirm("Distruggere definitivamente l'oggetto?")) return;
    setBusy(`d-${item_id}`);
    try {
      await api.gmPlayerRemoveItem(userId, item_id);
      toast.success("Distrutto");
      await onChanged(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-2" data-testid="equipment-editor">
      {["weapon", "armor", "trinket"].map(slot => {
        const eq = equipped?.[slot];
        const { label, Icon } = SLOT_META[slot];
        return (
          <div key={slot} className="panel-inner p-2 border border-[#333]" data-testid={`eq-slot-${slot}`}>
            <div className="flex items-center gap-2">
              <Icon className="w-3 h-3 text-[#E5C34A]" />
              <span className="font-heading text-[10px] tracking-widest uppercase text-[#E5C34A]">{label}</span>
              <span className="ml-auto font-mono text-[10px] text-[#B0ADA5]">
                {eq ? `${eq.name} · ${eq.rarity || "?"}` : "— vuoto —"}
              </span>
            </div>
            {eq && (
              <div className="flex justify-end gap-2 mt-2">
                <button onClick={() => doDestroy(eq.id)} disabled={busy === `d-${eq.id}`}
                  data-testid={`eq-destroy-${slot}`}
                  className="px-2 py-1 border border-[#8B3D3D] text-[#B85A5A] font-heading text-[9px] tracking-widest uppercase hover:bg-[#5D2323]/20 disabled:opacity-40 flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> Distruggi
                </button>
                <button onClick={() => doUnequip(slot)} disabled={busy === `u-${slot}`}
                  data-testid={`eq-unequip-${slot}`}
                  className="px-2 py-1 border border-[#B89C3A] text-[#E5C34A] font-heading text-[9px] tracking-widest uppercase hover:bg-[#B89C3A]/10 disabled:opacity-40">
                  Rimuovi
                </button>
              </div>
            )}
            {invBySlot[slot].length > 0 && (
              <div className="mt-2 pt-2 border-t border-[#262626] space-y-1">
                <div className="font-mono text-[8px] tracking-widest uppercase text-[#666]">
                  Equipaggia da inventario
                </div>
                {invBySlot[slot].map(it => (
                  <div key={it.id} className="flex items-center gap-2 text-[10px] font-mono text-[#B0ADA5]">
                    <span className="truncate flex-1">
                      {it.name} <span className="text-[#666]">· {it.rarity}</span>
                    </span>
                    <button onClick={() => doEquip(it.id)} disabled={busy === it.id}
                      data-testid={`eq-equip-${it.id}`}
                      className="px-2 py-1 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[9px] tracking-widest uppercase hover:bg-[#4A5D23]/10 disabled:opacity-40">
                      Equipa
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function InventoryEditor({ userId, inventory, gmItems, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [grantId, setGrantId] = useState("");
  const [grantQty, setGrantQty] = useState(1);
  const [gmFilter, setGmFilter] = useState("");

  const filteredGm = useMemo(() =>
    (gmItems || []).filter(i => !gmFilter || (i.name || "").toLowerCase().includes(gmFilter.toLowerCase())),
    [gmItems, gmFilter]
  );

  const doGrant = async () => {
    if (!grantId) { toast.error("Seleziona un oggetto"); return; }
    setBusy("grant");
    try {
      await api.gmPlayerGrantItem(userId, grantId, Number(grantQty || 1));
      toast.success("Assegnato");
      await onChanged(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(null); }
  };
  const doRemove = async (item_id) => {
    if (!window.confirm("Eliminare dall'inventario?")) return;
    setBusy(item_id);
    try {
      await api.gmPlayerRemoveItem(userId, item_id);
      toast.success("Rimosso");
      await onChanged(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-3" data-testid="inventory-editor">
      <div className="panel-inner p-2 border border-[#333] space-y-2">
        <div className="font-heading text-[10px] tracking-widest uppercase text-[#E5C34A] flex items-center gap-2">
          <Plus className="w-3 h-3" /> Assegna oggetto (dal catalogo GM)
        </div>
        <div className="flex items-center gap-2 border border-[#262626] px-2 py-1">
          <Search className="w-3 h-3 text-[#666]" />
          <input value={gmFilter} onChange={e => setGmFilter(e.target.value)}
            placeholder="Cerca oggetto GM…"
            data-testid="grant-search"
            className="flex-1 bg-transparent border-none text-xs text-[#E2DFD8] font-mono outline-none" />
        </div>
        <div className="relative">
          <select value={grantId} onChange={e => setGrantId(e.target.value)}
            data-testid="grant-item-select"
            className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] focus:border-[#B89C3A] outline-none appearance-none pr-6">
            <option value="">— seleziona —</option>
            {filteredGm.slice(0, 200).map(i => (
              <option key={i.id} value={i.id}>{i.name} · {i.rarity || "?"} · {i.type || "?"}</option>
            ))}
          </select>
          <ChevronDown className="w-3 h-3 text-[#666] absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <div className="flex items-end gap-2">
          <NumField label="Q.tà" value={grantQty} onChange={setGrantQty} testid="grant-qty" />
          <button onClick={doGrant} disabled={busy === "grant" || !grantId}
            data-testid="grant-submit"
            className="px-3 py-1.5 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10 disabled:opacity-40 flex items-center gap-1 h-fit">
            <Sparkles className="w-3 h-3" /> {busy === "grant" ? "…" : "Assegna"}
          </button>
        </div>
      </div>

      <div>
        <div className="font-heading text-[10px] tracking-widest uppercase text-[#E5C34A] flex items-center gap-2 mb-1">
          <Package className="w-3 h-3" /> Inventario ({inventory.length})
        </div>
        {inventory.length === 0 ? (
          <div className="text-xs italic text-[#666] py-3 text-center">Inventario vuoto.</div>
        ) : (
          <div className="space-y-1 max-h-[45vh] overflow-y-auto pr-1">
            {inventory.map(it => (
              <div key={it.id} className="panel-inner p-2 border border-[#262626] flex items-center gap-2 text-[10px] font-mono"
                data-testid={`inv-row-${it.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[#E2DFD8] truncate">
                    {it.name} <span className="text-[#666]">· {it.rarity} · {it.slot}</span>
                  </div>
                  {it.gm_effects?.length > 0 && (
                    <div className="text-[9px] text-[#8c8c8c] truncate">
                      {it.gm_effects.map(e => `${e.kind}${e.stat ? `:${e.stat}` : ""} +${e.value}`).join(" · ")}
                    </div>
                  )}
                </div>
                <button onClick={() => doRemove(it.id)} disabled={busy === it.id}
                  data-testid={`inv-remove-${it.id}`}
                  className="px-2 py-1 border border-[#8B3D3D] text-[#B85A5A] font-heading text-[9px] tracking-widest uppercase hover:bg-[#5D2323]/20 disabled:opacity-40 flex items-center gap-1">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Field primitives ────────────────────────────────────────────

function NumField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">{label}</div>
      <input type="number" value={value ?? 0} onChange={e => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] focus:border-[#B89C3A] outline-none" />
    </label>
  );
}
function TextField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">{label}</div>
      <input type="text" value={value ?? ""} onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] focus:border-[#B89C3A] outline-none" />
    </label>
  );
}
